/******************************************** STYLING *********************************************/
const s = require('./spinning-square-loader.scss');

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import moment from 'moment';
import {IfTruthy, uuid} from 'mad-utils/lib/react';
import {observable, action} from 'mobx';
import {observer, inject} from 'mobx-react';

/**************************************** PROJECT MODULES *****************************************/
import {StoreProps, ConfigProps} from '@canimmunize/core-imports';

const cln = `SpinningSquareLoaderContainerProps`;

/******************************************* CONTAINER ********************************************/
/**
 * Square spinning loader displayed during all data load actions after app
 * initialization completes
 *
 * Auto-detects loading status based on Javelin events (it's wired into Javelin)
 */
@inject('appState', 'config')
@observer
class SpinningSquareLoaderContainer extends React.Component<StoreProps & ConfigProps> {
    @observable
    private isActive = false;

    /**
     * Store UUIDs for each detected sync request (to deactivate after 45s)
     */
    private reqIDs: Set<string> = new Set();

    /**
     * Timeout handler functions for stopping the spinner if enough time passes
     * without load complete event being received
     *
     * Clear the handler functions when no longer needed
     */
    private timeoutHandles = [];
    private lastRequestTime: moment.Moment = null;

    /**
     * Register sync event listeners, set to activate & deactivate based on request status
     */
    constructor(props: StoreProps) {
        super(props);

        // Register events to show spinner when sync begins & hide spinner when sync finishes
        props.appState.jvlnsBroadcaster.subscribe(`ev_SYNC_START_FETCH`, jvlnsEvent => {
            this.setFailsafeShutoff();
            this.setSpinnerStatus(jvlnsEvent);
        });
        props.appState.jvlnsBroadcaster.subscribe(`ev_SYNC_COMPLETE`, this.setSpinnerStatus);
    }

    /**
     * Set current spinner status (including a failsafe to time it out after 30s)
     * based on current number of pending requests
     */
    private setSpinnerStatus = (jvlnsEvent: {pendingRequests?: number}) => {
        if (jvlnsEvent.pendingRequests === 0) {
            this.setInactive();
        } else {
            this.isActive = true;
        }
    };

    /**
     * Shut spinner off, including clearing all data related to timeout timing
     */
    @action
    private setInactive = () => {
        this.isActive = false;
        this.reqIDs.clear();
        this.timeoutHandles.forEach(handle => clearInterval(handle));
    };

    /**
     * Ensure spinner disappears 30 seconds after the incomplete request
     */
    private setFailsafeShutoff = () => {
        const reqID = uuid();
        this.reqIDs.add(reqID);
        this.lastRequestTime = moment();

        this.timeoutHandles.push(
            setTimeout(() => {
                if (this.reqIDs.has(reqID)) this.reqIDs.delete(reqID);
                if (this.reqIDs.size === 0) {
                    this.setInactive();
                    const lastRequestTime = this.lastRequestTime.clone();
                    const nowSafe = moment().add(100, `ms`);
                    if (
                        lastRequestTime
                            .add(this.props.config.animations.loadSpinnerTimeout, `ms`)
                            .isBefore(nowSafe)
                    ) {
                        this.props.appState.jvlnsBroadcaster.pendingRequests = 0;
                    }
                }
            }, this.props.config.animations.loadSpinnerTimeout)
        );
    };

    /**
     * Render spinner component UI
     */
    render() {
        return <SpinningSquareLoaderView isActive={this.isActive} />;
    }
}

/********************************************** VIEW **********************************************/
/**
 * Tiny spinning square (at top-right of content area) to indicate loading is
 * occurring
 *
 * Display if prop [isActive] is true, otherwise hide
 *
 * @param {boolean} isActive If true, display spinner
 */
export const SpinningSquareLoaderView = ({isActive = true}: {isActive?: boolean}) => (
    <IfTruthy test={isActive}>
        <div className={s[`loader-wrapper`]}>
            <span className={s[`loader`]}>
                <span className={s[`loader-inner`]} />
            </span>
        </div>
    </IfTruthy>
);

/********************************************* EXPORT *********************************************/
export {SpinningSquareLoaderContainer as SpinningSquareLoader};
